import { Flows, Folder } from '../../../../util/Types/Folder';
import { findRootFolderOf } from '../AgentsSidebar/folderTreeWalk';
import { isFluxbotFlow, isFormFlow, isVirtualFolderId } from '../utils';
import type {
  DragData,
  DropData,
  DropPosition,
  FolderDragData,
  ItemDragData,
  MoveOutcome,
  ReservedFolderType,
} from './types';

const RESERVED_NAMES: Record<string, ReservedFolderType> = {
  agents: 'agents',
  fluxbots: 'fluxbots',
  chatbot: 'chatbot',
  'vector memory': 'vector memory',
  objects: 'objects',
  shared: 'shared',
};

export const getReservedFolderType = (
  folder: Pick<Folder, 'id' | 'name'>,
): ReservedFolderType | null => {
  const lower = folder.name?.trim().toLowerCase() || '';
  if (lower in RESERVED_NAMES) return RESERVED_NAMES[lower];
  if (folder.id) {
    const match = folder.id.match(
      /-(Agents|Fluxbots|Vector Memory|Objects|Shared)$/,
    );
    if (match) {
      const cat = match[1].toLowerCase() as ReservedFolderType;
      if (
        cat === 'agents' ||
        cat === 'fluxbots' ||
        cat === 'objects' ||
        cat === 'shared'
      ) {
        return cat;
      }
      if (match[1] === 'Vector Memory') return 'vector memory';
    }
  }
  return null;
};

export const isFluxbotInVirtualHome = (
  flow: Flows,
  scopeFolders: Folder[],
): boolean => {
  if (!flow.folderId) return true;
  const root = findRootFolderOf(scopeFolders, flow.folderId);
  if (!root) return true;
  const reserved = getReservedFolderType(root);
  return reserved === 'fluxbots' || reserved === 'chatbot';
};

export const isLockedFolder = (
  folder: Pick<Folder, 'id' | 'name'>,
): boolean => {
  const reserved = getReservedFolderType(folder);
  if (reserved === 'shared') return true;
  const lower = folder.name?.trim().toLowerCase() || '';
  if (lower === 'data') return true;
  return false;
};

export const isReservedFolder = (
  folder: Pick<Folder, 'id' | 'name'>,
): boolean => getReservedFolderType(folder) !== null;

export const getFlowKind = (
  flow: Flows,
  rootFolderName?: string,
): 'agent' | 'form' | 'fluxbot' => {
  if (isFluxbotFlow(flow, rootFolderName)) return 'fluxbot';
  if (isFormFlow(flow)) return 'form';
  return 'agent';
};

const collectDescendantIds = (folder: Folder): string[] => {
  const ids: string[] = [];
  const visit = (f: Folder) => {
    f.folders?.forEach(sub => {
      ids.push(sub.id);
      visit(sub);
    });
  };
  visit(folder);
  return ids;
};

export const buildFolderDragData = (
  folder: Folder,
  ancestorReservedType: ReservedFolderType | null = null,
  spaceId?: string | null,
): FolderDragData => ({
  kind: 'folder',
  folderId: folder.id,
  parentId: folder.parentId ?? null,
  spaceId: spaceId ?? folder.space ?? null,
  reservedType: getReservedFolderType(folder),
  ancestorReservedType,
  isVirtual: isVirtualFolderId(folder.id),
  isLocked: isLockedFolder(folder),
  descendantFolderIds: collectDescendantIds(folder),
});

export const buildItemDragData = (
  flow: Flows,
  parentId: string | null,
  parentFolderName?: string,
  ancestorReservedType: ReservedFolderType | null = null,
): ItemDragData => ({
  kind: getFlowKind(flow, parentFolderName),
  itemId: flow.id,
  parentId,
  spaceId:
    flow.spaceId ??
    (typeof flow.space === 'string' ? flow.space : null) ??
    null,
  ancestorReservedType,
});

const isSharedRooted = (
  reservedType: ReservedFolderType | null,
  ancestorReservedType: ReservedFolderType | null,
): boolean => reservedType === 'shared' || ancestorReservedType === 'shared';

export const canDrop = (
  active: DragData,
  over: DropData,
  position?: DropPosition | null,
): MoveOutcome => {
  if (active.kind === 'folder' && active.reservedType) {
    return {
      ok: false,
      reason: 'This folder is locked and cannot be moved.',
    };
  }

  const dragShared =
    active.kind === 'folder'
      ? isSharedRooted(active.reservedType, active.ancestorReservedType)
      : isSharedRooted(null, active.ancestorReservedType);
  const dropShared =
    over.kind === 'folder-drop'
      ? isSharedRooted(over.reservedType, over.ancestorReservedType)
      : over.kind === 'item-drop'
        ? isSharedRooted(over.reservedType, over.ancestorReservedType)
        : false;
  if (dragShared && !dropShared) {
    return {
      ok: false,
      reason: 'Items inside Shared cannot be moved outside it.',
    };
  }

  if (over.kind === 'root-drop') {
    if (active.kind !== 'folder') {
      return { ok: false };
    }
    if (active.spaceId !== over.spaceId) {
      return {
        ok: false,
        reason: 'Items cannot be moved between spaces.',
      };
    }
    return { ok: true };
  }

  if (over.kind === 'item-drop') {
    const activeId = active.kind === 'folder' ? active.folderId : active.itemId;
    if (activeId === over.itemId) return { ok: false };
    if (active.spaceId !== over.spaceId) {
      return { ok: false, reason: 'Items cannot be moved between spaces.' };
    }
    if (
      active.kind === 'folder' &&
      active.descendantFolderIds.includes(over.itemId)
    ) {
      return {
        ok: false,
        reason: 'A folder cannot be moved into one of its own subfolders.',
      };
    }
    return { ok: true };
  }

  if (over.isLocked) return { ok: false, reason: 'This folder is locked.' };

  if (active.spaceId !== over.spaceId) {
    return { ok: false, reason: 'Items cannot be moved between spaces.' };
  }

  const isInsideDrop = position === 'inside' || position == null;

  if (active.kind === 'folder') {
    if (active.folderId === over.folderId) return { ok: false };
    if (active.descendantFolderIds.includes(over.folderId)) {
      return {
        ok: false,
        reason: 'A folder cannot be moved into one of its own subfolders.',
      };
    }
    if (active.parentId === over.folderId && isInsideDrop) {
      return { ok: false, reason: 'This item is already in this folder.' };
    }
  } else if (active.parentId === over.folderId && isInsideDrop) {
    return { ok: false, reason: 'This item is already in this folder.' };
  }

  return { ok: true };
};
